import {useEffect, useState} from "react";
import {DeleteOutlined} from "@ant-design/icons";
import {Modal} from "antd";
import {CorrectDepartmentItem} from "../types/department";
import {departmentsService} from "../services/departments";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";

export const useDepartmentController = () => {
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [selectedDepartment, setSelectedDepartment] = useState<CorrectDepartmentItem | null>(null)
  const [departments, setDepartments] = useState<CorrectDepartmentItem[]>([])

  useEffect(() => {
    if (!isDepartmentModalOpen && selectedDepartment) {
      setSelectedDepartment(null)
    }
  }, [isDepartmentModalOpen])

  useEffect(() => {
    fetchDepartmentsList([])
  }, [lastUpdate])

  const confirmDeleteDepartment = (props: { departmentId: string, departmentName: string }) => {
    const {departmentId, departmentName} = props
    const {confirm} = Modal;
    confirm(
      {
        width: 600,
        icon: <DeleteOutlined/>,
        title: `Видалити "${departmentName}" і все що з ним пов'язано?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          deleteDepartmentHandler(departmentId)
        },
      });

  }

  const deleteDepartmentHandler = async (id: string) => {
    setIsLoading(true)
    try {
      await departmentsService.delete(id)
      openSuccessNotification("Відділення успішно видалено!")
      changeLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка при видаленні відділення!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const changeLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const toggleDepartmentModalOpen = () => {
    setIsDepartmentModalOpen(!isDepartmentModalOpen)
  }

  const fetchDepartmentsList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await departmentsService.list(queries)
      setDepartments(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при завантаженні відділень!"})
    } finally {
      setIsLoading(false)
    }
  }

  const createDepartment = async (data: CorrectDepartmentItem) => {
    setIsLoading(true)
    try {
      const createdDepartment = await departmentsService.createDepartment(data)
      openSuccessNotification("Відділення успішно створено!")
      toggleDepartmentModalOpen()
      changeLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка при створенні відділення!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const updateDepartment = async (data: CorrectDepartmentItem) => {
    setIsLoading(true)
    try {
      const updatedDepartment = await departmentsService.updateDepartment(data)
      toggleDepartmentModalOpen()
      changeLastUpdate()
      openSuccessNotification("Відділення успішно оновлено!")

    } catch (error) {
      showErrorNotification({message: "Помилка при оновленні відділення!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const getDepartmentItemById = async (id: string) => {
    setIsLoading(true)
    try {
      const currentDepartment = await departmentsService.geDepartmentItem(id)
      setSelectedDepartment(currentDepartment)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при отриманні даних!"})
    } finally {
      setIsLoading(false)
    }
  }

  const editButtonHandler = (department: CorrectDepartmentItem) => {
    setSelectedDepartment(department)
    toggleDepartmentModalOpen()
  }

  return {
    isLoading,
    confirmDeleteDepartment,
    toggleDepartmentModalOpen,
    isDepartmentModalOpen,
    createDepartment,
    fetchDepartmentsList,
    departments,
    editButtonHandler,
    selectedDepartment,
    updateDepartment,
    getDepartmentItemById
  }
}
